import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Label } from "@themes/pollinator/ui/Label";
import { OrgLookupInput } from "@themes/pollinator/ui/OrgLookupInput";
import { OptionType } from "@core/types/select.types";

// Schema for organization select - uses OptionType shape
const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
  data: z.unknown().optional(),
  __isNew__: z.boolean().optional(),
});

const schema = z.object({
  organization: optionSchema.nullable(),
});

export type Props = {
  isOpen: boolean;
  options: OptionType[];
  selectedOptions: OptionType[];
  selectedOrg: OptionType | null;
  isLoading: boolean;
  onClose: () => void;
  onSelectOrganization: (option: OptionType | null) => void;
  onCreateOrganization: (inputValue: string) => void;
  /** Render function for selected option items */
  renderSelectedItem: (option: OptionType) => React.ReactNode;
};

/**
 * Dialog for looking up and selecting organizations.
 * Pure presentational component - receives all state and callbacks via props.
 */
export function OrgLookupDialog({
  isOpen,
  options,
  selectedOptions,
  selectedOrg,
  isLoading,
  onClose,
  onSelectOrganization,
  onCreateOrganization,
  renderSelectedItem,
}: Props) {
  // Form context needed for OrgLookupInput (uses useFormContext internally)
  const { form } = useForm({
    schema,
    defaultValues: { organization: null },
    onSubmit: () => {},
  });

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="No invite code?"
      description="We accept alumni from vetted cross-disciplinary institutions and programs."
      isWithActions={false}
    >
      <Form form={form}>
        <div
          className={twMerge(
            "flex flex-col gap-8",
            selectedOptions.length === 0 ? "mb-40" : "mb-8"
          )}
        >
          <OrgLookupInput
            name="organization"
            onSelect={onSelectOrganization}
            onCreate={onCreateOrganization}
            options={options}
            value={selectedOrg}
            isLoading={isLoading}
            className="px-0"
          />
          {selectedOptions.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label>Selected Organizations</Label>
              <div className="flex flex-col gap-2">
                {selectedOptions.map((option) => renderSelectedItem(option))}
              </div>
            </div>
          )}
        </div>
      </Form>
    </Dialog>
  );
}

export default OrgLookupDialog;
